import React, { useCallback, useEffect } from "react"
import { Box, Flex } from "@chakra-ui/react"

import { useCore } from "@app/hooks/useCore"
import { useNavigation } from "@app/hooks/useNavigation"
import { useAppContext } from "@app/providers/app"
import { useMedia } from "@app/hooks/useMedia"
import Brand from "@app/components/Brand"
import { CartWidget } from "@app/components/Cart/CartWidget"
import WishlistWidget from "@app/components/Wishlist/WishlistWidget"
import AccountWidget from "@app/components/Account/Widget/AccountWidget"
import SearchWidget from "@app/components/Search/Widget/SearchWidget"
import SearchForm from "@app/components/Search/Widget/SearchForm"
import NavigationHeaderWidget from "@app/components/Navigation/Header/NavigationHeaderWidget"
import NavigationHeader from "@app/components/Navigation/Header/NavigationHeader"
import NavigationHeaderMenu from "@app/components/Navigation/Header/NavigationHeaderMenu"
import HeaderScrollContainer from "@app/components/HeaderScrollContainer"
import Link from "@app/components/Link"
import Announcement from "@app/components/Announcement"

import { Location } from "@root/types/global"

const SearchDropdown = React.lazy(() => import("@app/components/Search/Widget/SearchDropdown"))

type Props = {
  isHomePage?: boolean
  location: Location
}

const Header: React.FC<Props> = ({ isHomePage, location }) => {
  const { state, dispatch, headerRef, navBarRef, announcementRef } = useAppContext()
  const { isMedium, isHydrated } = useMedia()

  const {
    helpers: { isBrowser },
  } = useCore()

  const { mobilePrimary, mobileSecondary, desktopPrimary } = useNavigation()

  const onHeaderToggle = useCallback(
    (payload: any) => {
      dispatch({
        type: "header",
        payload: payload,
      })
    },
    [dispatch]
  )

  const onLogoClick = useCallback(() => {
    dispatch({
      type: "header",
      payload: null,
    })
  }, [dispatch])

  useEffect(() => {
    if (isMedium && state.activeMenu) {
      dispatch({
        type: "menu",
        payload: false,
      })
    }
  }, [isMedium, state.activeMenu, dispatch])

  return (
    <>
      <Box ref={announcementRef}>
        <Announcement location={location} />
      </Box>

      <HeaderScrollContainer headerRef={headerRef}>
        <Flex
          pos="relative"
          justifyContent="center"
          width="100%"
          px={{ base: 5, md: 10, lg: 14 }}
          onClick={e => {
            if (
              (state.activeHeader && e.target === e.currentTarget) ||
              (state.activeHeader && e.target instanceof Node && e.currentTarget.contains(e.target))
            ) {
              onHeaderToggle(null)
            }
          }}
        >
          {/* Desktop: Hide, Mobile/Tablet: Center */}
          <Flex display={{ base: "flex", lg: "none" }} pos="absolute" top="50%" transform="translate(0, -50%)">
            <Link to="/" onClick={onLogoClick}>
              <Brand
                color="primary.midGrey"
                animationMode="hover"
                animationBaseDotColor="preset.blueAccent"
                width={26}
                tag={isHomePage ? "h1" : "div"}
              />
            </Link>
          </Flex>
          <Flex ref={navBarRef} width="100%" h={{ base: 16, md: 15 }} justify="space-between" align="center">
            {/* Desktop: Left, Mobile: Hide */}
            <Flex display={{ base: "none", lg: "flex" }}>
              <Link to="/" onClick={onLogoClick}>
                <Brand
                  color="primary.midGrey"
                  animationMode="hover"
                  animationBaseDotColor="preset.blueAccent"
                  width={32.5}
                  height={8}
                  tag={isHomePage ? "h1" : "div"}
                />
              </Link>
            </Flex>

            {/* Desktop: Center, Mobile: Left */}
            <Flex alignItems="center" display={{ base: "flex", lg: "none" }}>
              <NavigationHeaderWidget />
              <Box display={{ base: "none", md: "block", lg: "none" }}>
                <SearchWidget color={"primary.midGrey"} />
              </Box>
            </Flex>

            {/* Desktop: Right, Mobile: Right */}
            {(!isHydrated || isMedium) && <NavigationHeader items={desktopPrimary} onHeaderToggle={onHeaderToggle} />}
            <Flex justifyContent="flex-end" alignItems="center">
              <Box display={{ base: "none", lg: "block" }} onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                <SearchWidget color="primary.midGrey" />
              </Box>
              <AccountWidget display={{ base: "none", lg: "block" }} color="primary.midGrey" />
              <WishlistWidget color="primary.midGrey" />
              <CartWidget light={false} />
            </Flex>
          </Flex>
        </Flex>

        {/* Mobile SearchForm */}
        <Box bg="primary.white" transition="top 0.25s ease" display={{ base: "block", md: "none" }}>
          <SearchForm />
        </Box>

        <React.Suspense fallback={<div />}>{state.activeSearch && <SearchDropdown />}</React.Suspense>
      </HeaderScrollContainer>
      {isBrowser && <NavigationHeaderMenu items={mobilePrimary} footerItems={mobileSecondary} isOpen={state.activeMenu} />}
    </>
  )
}

export default React.memo(Header)
